import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const Terms = () => {
  const termsStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Terms of Service - Digital Shark",
    "description": "Terms of service for Digital Shark Inc., outlining the terms and conditions for using our services.",
    "url": "https://digitalshark.tv/terms",
    "isPartOf": {
      "@type": "WebSite",
      "name": "Digital Shark",
      "url": "https://digitalshark.tv"
    }
  };

  return (
    <div className="min-h-screen pt-20">
      <SEO 
        title="Terms of Service"
        description="Terms of service for Digital Shark Inc., outlining the terms and conditions for using our services."
        keywords="terms of service, Digital Shark terms, service agreement, terms and conditions, Caribbean terms, Barbados terms"
        url="/terms"
        structuredData={termsStructuredData}
      />
      
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary-900 to-primary-800 text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Terms of Service
            </h1>
            <p className="text-xl text-gray-300">
              Terms and conditions for using Digital Shark services
            </p>
            <p className="text-sm text-gray-400 mt-4">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="prose prose-lg max-w-none"
            >
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-primary-800 mb-6">Agreement to Terms</h2>
                <p className="text-primary-600 mb-6">
                  By accessing and using Digital Shark Inc.'s services, you accept and agree to be bound by the terms 
                  and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
                </p>

                <h2 className="text-2xl font-bold text-primary-800 mb-6">Services Description</h2>
                <p className="text-primary-600 mb-4">
                  Digital Shark Inc. provides the following services:
                </p>
                <ul className="list-disc list-inside text-primary-600 mb-6 space-y-2">
                  <li>Live broadcast and streaming services</li>
                  <li>Television show and documentary production</li>
                  <li>Pay Per View (PPV) systems</li>
                  <li>Rights and licensing distribution</li>
                  <li>Video production and post-production services</li>
                </ul>

                <h2 className="text-2xl font-bold text-primary-800 mb-6">Service Agreements</h2>
                <p className="text-primary-600 mb-4">
                  All services are provided subject to:
                </p>
                <ul className="list-disc list-inside text-primary-600 mb-6 space-y-2">
                  <li>Written service agreements for each project</li>
                  <li>Payment terms as specified in individual contracts</li>
                  <li>Delivery timelines agreed upon in advance</li>
                  <li>Quality standards as outlined in project specifications</li>
                </ul>

                <h2 className="text-2xl font-bold text-primary-800 mb-6">Payment Terms</h2>
                <p className="text-primary-600 mb-4">
                  Payment terms include:
                </p>
                <ul className="list-disc list-inside text-primary-600 mb-6 space-y-2">
                  <li>Deposit requirements as specified in service agreements</li>
                  <li>Payment schedules based on project milestones</li>
                  <li>Late payment fees as outlined in contracts</li>
                  <li>Currency and payment method specifications</li>
                </ul>

                <h2 className="text-2xl font-bold text-primary-800 mb-6">Intellectual Property</h2>
                <p className="text-primary-600 mb-6">
                  All content, materials, and intellectual property created by Digital Shark Inc. remain the property 
                  of Digital Shark Inc. unless otherwise specified in writing. Client retains rights to their original 
                  content and materials provided to us.
                </p>

                <h2 className="text-2xl font-bold text-primary-800 mb-6">Limitation of Liability</h2>
                <p className="text-primary-600 mb-6">
                  Digital Shark Inc. shall not be liable for any indirect, incidental, special, consequential, or 
                  punitive damages, including without limitation, loss of profits, data, use, goodwill, or other 
                  intangible losses, resulting from your use of our services.
                </p>

                <h2 className="text-2xl font-bold text-primary-800 mb-6">Force Majeure</h2>
                <p className="text-primary-600 mb-6">
                  Digital Shark Inc. shall not be liable for any failure or delay in performance under this agreement 
                  which is due to fire, flood, earthquake, elements of nature, acts of God, acts of war, terrorism, 
                  or other causes beyond our reasonable control.
                </p>

                <h2 className="text-2xl font-bold text-primary-800 mb-6">Governing Law</h2>
                <p className="text-primary-600 mb-6">
                  This agreement shall be governed by and construed in accordance with the laws of Barbados. 
                  Any disputes arising from this agreement shall be subject to the exclusive jurisdiction of 
                  the courts of Barbados.
                </p>

                <h2 className="text-2xl font-bold text-primary-800 mb-6">Modifications</h2>
                <p className="text-primary-600 mb-6">
                  Digital Shark Inc. reserves the right to modify these terms at any time. We will notify clients 
                  of any material changes to these terms. Continued use of our services after such modifications 
                  constitutes acceptance of the updated terms.
                </p>

                <h2 className="text-2xl font-bold text-primary-800 mb-6">Contact Information</h2>
                <p className="text-primary-600 mb-4">
                  For questions regarding these Terms of Service, please contact us:
                </p>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-primary-600 mb-2">
                    <strong>Email:</strong> admin@digitalshark.tv
                  </p>
                  <p className="text-primary-600 mb-2">
                    <strong>Phone:</strong> +1-265-250-0424
                  </p>
                  <p className="text-primary-600">
                    <strong>Address:</strong> French Village, St. Peter, Barbados
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;
